import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Tenant, User } from "@/lib/api";
import { apiClient } from "@/lib/api";
import { TokenManager } from "@/lib/secure-storage";

interface AuthState {
	user: User | null;
	tenant: Tenant | null;
	token: string | null;
	isLoading: boolean;
	isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

interface AuthProviderProps {
	children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
	const [authState, setAuthState] = useState<AuthState>({
		user: null,
		tenant: null,
		token: null,
		isLoading: true,
		isAuthenticated: false,
	});

	const navigate = useNavigate();
	const location = useLocation();

	// Initialize auth from secure storage
	useEffect(() => {
		const initializeAuth = async () => {
			try {
				const tokenManager = new TokenManager();
				const token = tokenManager.getToken();
				const userStr = localStorage.getItem("artify_user");
				const tenantStr = localStorage.getItem("artify_tenant");

				if (token && userStr && tenantStr) {
					const user = JSON.parse(userStr);
					const tenant = JSON.parse(tenantStr);

					// Verify token is still valid via API
					const tokenVerification = await apiClient.verifyToken();

					if (tokenVerification.valid) {
						setAuthState({
							user: tokenVerification.user || user,
							tenant,
							token,
							isLoading: false,
							isAuthenticated: true,
						});
					} else {
						// Token expired, clear storage
						const tokenManager = new TokenManager();
						tokenManager.removeToken();
						localStorage.removeItem("artify_user");
						localStorage.removeItem("artify_tenant");
						setAuthState({
							user: null,
							tenant: null,
							token: null,
							isLoading: false,
							isAuthenticated: false,
						});
					}
				} else {
					setAuthState({
						user: null,
						tenant: null,
						token: null,
						isLoading: false,
						isAuthenticated: false,
					});
				}
			} catch (error) {
				console.error("Auth initialization failed:", error);
				// Clear storage on error and set unauthenticated state
				const tokenManager = new TokenManager();
				tokenManager.removeToken();
				localStorage.removeItem("artify_user");
				localStorage.removeItem("artify_tenant");
				setAuthState({
					user: null,
					tenant: null,
					token: null,
					isLoading: false,
					isAuthenticated: false,
				});
			}
		};

		initializeAuth();
	}, []);

	// Redirect to login if not authenticated and not on public pages
	useEffect(() => {
		const publicPaths = [
			"/login",
			"/onboarding",
			"/auth-demo",
			"/demo",
			"/standalone-demo",
		];
		const isPublicPath = publicPaths.some((path) =>
			location.pathname.startsWith(path),
		);

		if (!authState.isLoading && !authState.isAuthenticated && !isPublicPath) {
			navigate("/login", { replace: true });
		}
	}, [
		authState.isAuthenticated,
		authState.isLoading,
		location.pathname,
		navigate,
	]);

	const login = async (email: string, password: string) => {
		try {
			setAuthState((prev) => ({ ...prev, isLoading: true }));

			// Use the canonical API client for authentication
			const loginResponse = await apiClient.login({ email, password });

			// Calculate token expiration time in minutes
			const expiresAt = new Date(loginResponse.expiresAt);
			const now = new Date();
			const expirationMinutes = Math.floor(
				(expiresAt.getTime() - now.getTime()) / 60000,
			);

			// Store securely using TokenManager
			const tokenManager = new TokenManager();
			tokenManager.setToken(loginResponse.token, expirationMinutes);
			localStorage.setItem("artify_user", JSON.stringify(loginResponse.user));
			localStorage.setItem(
				"artify_tenant",
				JSON.stringify(loginResponse.tenant),
			);

			// Store refresh token if provided
			if (loginResponse.refreshToken) {
				localStorage.setItem(
					"artify_refresh_token",
					loginResponse.refreshToken,
				);
			}

			setAuthState({
				user: loginResponse.user,
				tenant: loginResponse.tenant,
				token: loginResponse.token,
				isLoading: false,
				isAuthenticated: true,
			});

			navigate("/dashboard");
		} catch (error) {
			setAuthState((prev) => ({
				...prev,
				isLoading: false,
			}));
			throw error;
		}
	};

	const logout = async () => {
		try {
			// Call API to logout (invalidate token on server)
			await apiClient.logout();
		} catch (error) {
			console.warn("Logout API call failed:", error);
		} finally {
			// Always clear local storage regardless of API call result
			const tokenManager = new TokenManager();
			tokenManager.removeToken();
			localStorage.removeItem("artify_user");
			localStorage.removeItem("artify_tenant");
			localStorage.removeItem("artify_refresh_token");

			setAuthState({
				user: null,
				tenant: null,
				token: null,
				isLoading: false,
				isAuthenticated: false,
			});

			navigate("/login");
		}
	};

	const refreshAuth = async () => {
		try {
			const tokenManager = new TokenManager();
			const token = tokenManager.getToken();

			if (!token) return;

			// Use canonical API client to refresh token
			const refreshResponse = await apiClient.refreshToken();

			// Calculate token expiration time in minutes
			const expiresAt = new Date(refreshResponse.expiresAt);
			const now = new Date();
			const expirationMinutes = Math.floor(
				(expiresAt.getTime() - now.getTime()) / 60000,
			);

			// Store securely using TokenManager
			tokenManager.setToken(refreshResponse.token, expirationMinutes);

			setAuthState((prev) => ({
				...prev,
				token: refreshResponse.token,
			}));
		} catch (error) {
			console.error("Auth refresh failed:", error);
			await logout();
		}
	};

	const value: AuthContextType = {
		...authState,
		login,
		logout,
		refreshAuth,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
